import { Check, Upload } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { FileUpload } from '@/components/ui/file-upload'
import { type RequiredDocument, StepStatus } from './checkoutData'
import { StepStatusBadge } from './StepStatusBadge'

// Mock: how long the awaiting service takes to send its documents in.
const AWAITED_ARRIVAL_MS = 10_000

/** One row per document the provider needs; the step is complete once every one is in. */
export function DocumentUploadStep({
  carrierName,
  documents,
  onStatusChange,
  canAdvance,
  onNext,
}: {
  carrierName: string
  documents: RequiredDocument[]
  onStatusChange: (status: StepStatus) => void
  /** Whether Next step has somewhere to go, given whether every document is in. */
  canAdvance: (isComplete: boolean) => boolean
  onNext: (isComplete: boolean) => void
}) {
  const [uploaded, setUploaded] = useState<Record<string, string>>({})
  const [isAwaitedReceived, setIsAwaitedReceived] = useState(false)
  const isReceived = (awaitingFrom?: string) => isAwaitedReceived && !!awaitingFrom
  const isAllComplete = documents.every(
    ({ name, isComplete, awaitingFrom }) => isComplete || uploaded[name] || isReceived(awaitingFrom)
  )

  useEffect(() => {
    if (!documents.some(({ awaitingFrom }) => awaitingFrom)) return
    const timer = setTimeout(() => {
      setIsAwaitedReceived(true)
      // One id per document, so cards waiting on the same service share a single toast.
      for (const { name, awaitingFrom } of documents) {
        if (awaitingFrom) toast.success(`${name} received`, { id: `${name}-received` })
      }
    }, AWAITED_ARRIVAL_MS)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (isAwaitedReceived) onStatusChange(isAllComplete ? StepStatus.COMPLETE : StepStatus.MISSING_INFO)
  }, [isAwaitedReceived])

  function upload(document: string, file: File) {
    const next = { ...uploaded, [document]: file.name }
    setUploaded(next)
    const isComplete = documents.every(
      ({ name, isComplete, awaitingFrom }) => isComplete || next[name] || isReceived(awaitingFrom)
    )
    onStatusChange(isComplete ? StepStatus.COMPLETE : StepStatus.MISSING_INFO)
  }

  return (
    <div className="mt-3 flex flex-col gap-3">
      <p className="text-ink-500 text-sm lg:max-w-[400px]">
        {carrierName} needs these before it can activate your coverage.
      </p>
      <ul className="border-ink-100 divide-ink-100 divide-y rounded-sm border">
        {documents.map(({ name, isComplete, awaitingFrom }) => (
          <li key={name} className="flex items-center justify-between gap-3 px-3 py-2">
            <span className="flex min-w-0 flex-col">
              <span className="text-sm font-semibold">{name}</span>
              {uploaded[name] && (
                <span className="text-ink-500 flex items-center gap-1 truncate text-xs">
                  <Check className="size-3 shrink-0 text-green-600" />
                  {uploaded[name]}
                </span>
              )}
            </span>
            {isComplete || uploaded[name] || isReceived(awaitingFrom) ? (
              <StepStatusBadge status={StepStatus.COMPLETE} />
            ) : (
              <span className="flex flex-wrap items-center justify-end gap-2 text-right">
                {awaitingFrom && (
                  <>
                    <span className="text-ink-500 text-xs italic">
                      Waiting for response from {awaitingFrom}
                    </span>
                    <span className="text-ink-400 text-sm">or</span>
                  </>
                )}
                <FileUpload
                  className="flex"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(file) => {
                    if (file instanceof File) upload(name, file)
                  }}
                >
                  <Button variant="link" size="sm" className="h-auto p-0">
                    <Upload />
                    Upload {name}
                  </Button>
                </FileUpload>
              </span>
            )}
          </li>
        ))}
      </ul>
      <Button
        className="self-start"
        disabled={!canAdvance(isAllComplete)}
        onClick={() => onNext(isAllComplete)}
      >
        Next step
      </Button>
    </div>
  )
}
